import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronRight, Tag, Package } from 'lucide-react';
import { catalogProducts } from '../data/catalog';
import { slugify } from '../utils/slugs';

// Mapping des catégories vers les slugs
const categoryToSlug = {
  'Evenementiel - Barnum': 'evenementiel-barnum',
  'Bricolage - Travaux': 'bricolage-travaux',
  'Jardinage - Motoculture': 'jardinage-motoculture',
  'Nettoyage - Logement': 'nettoyage-logement'
};

// Fonction pour normaliser le texte (retirer les accents)
const normalizeText = (text) => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const SearchBar = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (query.length >= 2) {
      const normalizedQuery = normalizeText(query);
      const searchTerms = normalizedQuery.split(' ').filter(t => t.length > 0);
      
      const filtered = catalogProducts.filter(product => {
        const searchText = normalizeText(`${product.name} ${product.category} ${product.subcategory}`);
        return searchTerms.every(term => searchText.includes(term));
      });
      
      // Sort by relevance - exact name matches first
      filtered.sort((a, b) => {
        const aNameMatch = normalizeText(a.name).includes(normalizedQuery);
        const bNameMatch = normalizeText(b.name).includes(normalizedQuery);
        if (aNameMatch && !bNameMatch) return -1;
        if (!aNameMatch && bNameMatch) return 1;
        return 0;
      });
      
      setResults(filtered.slice(0, 10));
      setSelectedIndex(-1);
    } else {
      setResults([]);
    }
  }, [query]);

  const handleProductClick = (product) => {
    onClose();
    // Naviguer vers la page des équipements avec la sous-catégorie (slug SEO)
    const categorySlug = categoryToSlug[product.category] || 'evenementiel-barnum';
    const subcategorySlug = slugify(product.subcategory);
    window.location.href = `/equipements/${categorySlug}/${subcategorySlug}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      handleProductClick(results[selectedIndex]);
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  const formatPrice = (price) => {
    return price > 0 ? `${price.toFixed(2)}€` : 'Sur devis';
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Evenementiel': return 'bg-green-100 text-green-700';
      case 'Packs': return 'bg-purple-100 text-purple-700';
      case 'Accessoires': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Search Modal */}
      <div className="relative max-w-2xl mx-auto mt-20 px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={22} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher un matériel (minipelle, broyeur, chapiteau...)"
              className="w-full pl-12 pr-12 py-5 text-lg border-0 focus:outline-none focus:ring-0"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X size={20} className="text-slate-400" />
              </button>
            )}
          </div>

          {/* Results */}
          {query.length >= 2 && (
            <div className="border-t border-slate-100">
              {results.length > 0 ? (
                <div 
                  ref={resultsRef}
                  className="max-h-96 overflow-y-auto"
                >
                  {results.map((product, index) => (
                    <div
                      key={product.id}
                      className={`flex items-center gap-4 px-4 py-3 cursor-pointer transition-colors ${
                        index === selectedIndex 
                          ? 'bg-green-50' 
                          : 'hover:bg-slate-50'
                      }`}
                      onClick={() => handleProductClick(product)}
                    >
                      {/* Icon */}
                      <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Package className="text-slate-400" size={24} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-800 truncate">
                            {product.name}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(product.category)}`}>
                            {product.subcategory}
                          </span>
                          {product.quantity > 1 && (
                            <span className="text-slate-400">
                              {product.quantity} dispo.
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right flex-shrink-0">
                        <div className="font-bold text-green-500">
                          {formatPrice(product.priceWeekend)}
                        </div>
                        <div className="text-xs text-slate-400">
                          /weekend
                        </div>
                      </div>

                      {/* Arrow */}
                      <ChevronRight className="text-slate-300 flex-shrink-0" size={20} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-slate-500">
                  <Package className="mx-auto mb-3 text-slate-300" size={40} />
                  <p>Aucun matériel trouvé pour "{query}"</p>
                  <p className="text-sm mt-1">Essayez avec d'autres termes</p>
                </div>
              )}
            </div>
          )}

          {/* Quick suggestions when empty */}
          {query.length < 2 && (
            <div className="border-t border-slate-100 px-4 py-4">
              <p className="text-sm text-slate-500 mb-3">Suggestions populaires</p>
              <div className="flex flex-wrap gap-2">
                {['Chapiteau', 'Minipelle', 'Broyeur', 'Shampouineuse', 'Bétonnière', 'Gonflable', 'Motoculteur', 'Nettoyeur HP'].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => setQuery(suggestion)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-green-100 hover:text-green-700 rounded-lg text-sm font-medium transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-slate-100 px-4 py-3 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
            <div className="flex items-center gap-4">
              <span><kbd className="px-1.5 py-0.5 bg-white rounded border">↑</kbd> <kbd className="px-1.5 py-0.5 bg-white rounded border">↓</kbd> pour naviguer</span>
              <span><kbd className="px-1.5 py-0.5 bg-white rounded border">Entrée</kbd> pour sélectionner</span>
            </div>
            <span><kbd className="px-1.5 py-0.5 bg-white rounded border">Échap</kbd> pour fermer</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
